import React, { useState } from 'react';
import AdminLayout from '../AdminLayout/AdminLayout';
import { Card, Table, Button, Modal, Form, Input, message, Space, Tag, Spin } from 'antd';
import { FaPlus, FaEdit, FaTrash, FaStethoscope } from 'react-icons/fa';
import { useGetDoctorsQuery } from '../../../redux/api/doctorApi';
import {
    useGetSpecialtiesQuery,
    useCreateSpecialtyMutation,
    useUpdateSpecialtyMutation,
    useDeleteSpecialtyMutation,
} from '../../../redux/api/specialtyApi';
import './Specialites.css';

const Specialites = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSpecialty, setEditingSpecialty] = useState(null);
    const [form] = Form.useForm();

    const { data: specialtiesData, isLoading: specialtiesLoading } = useGetSpecialtiesQuery();
    const { data: doctorsData } = useGetDoctorsQuery({ limit: 100 });
    const [createSpecialty, { isLoading: creating }] = useCreateSpecialtyMutation();
    const [updateSpecialty, { isLoading: updating }] = useUpdateSpecialtyMutation();
    const [deleteSpecialty] = useDeleteSpecialtyMutation();

    const specialties = specialtiesData || [];
    const doctors = doctorsData?.doctors || [];

    const specialtiesWithCount = specialties.map(spec => ({
        ...spec,
        doctorCount: doctors.filter(d => d.specialization === spec.name).length,
    }));

    const handleAdd = () => {
        setEditingSpecialty(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (specialty) => {
        setEditingSpecialty(specialty);
        form.setFieldsValue({ name: specialty.name });
        setIsModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            if (editingSpecialty) {
                await updateSpecialty({ id: editingSpecialty.id, data: { name: values.name } }).unwrap();
                message.success('Specialty updated');
            } else {
                await createSpecialty({ name: values.name }).unwrap();
                message.success('Specialty created');
            }
            setIsModalVisible(false);
            form.resetFields();
            setEditingSpecialty(null);
        } catch (err) {
            message.error(err?.data?.message || 'Failed to save specialty');
        }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: 'Delete Specialty',
            content: 'Are you sure you want to delete this specialty?',
            onOk: async () => {
                try {
                    await deleteSpecialty(id).unwrap();
                    message.success('Specialty deleted');
                } catch (err) {
                    message.error(err?.data?.message || 'Failed to delete specialty');
                }
            },
        });
    };

    const columns = [
        {
            title: 'Specialty',
            dataIndex: 'name',
            key: 'name',
            render: (text) => (
                <div className="specialty-name">
                    <FaStethoscope className="specialty-icon" />
                    {text}
                </div>
            ),
        },
        {
            title: 'Doctors',
            dataIndex: 'doctorCount',
            key: 'doctorCount',
            width: 150,
            render: (count) => (
                <Tag color={count > 0 ? 'blue' : 'default'}>
                    {count} doctor{count !== 1 ? 's' : ''}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<FaEdit />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Button
                        type="link"
                        danger
                        icon={<FaTrash />}
                        onClick={() => handleDelete(record.id)}
                        size="small"
                    />
                </Space>
            ),
        },
    ];

    if (specialtiesLoading) {
        return (
            <AdminLayout title="Specialties" breadcrumbs={['Admin', 'Specialties']}>
                <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Specialties" breadcrumbs={['Admin', 'Specialties']}>
            <Card className="admin-card">
                <div className="table-toolbar">
                    <div></div>
                    <Button
                        type="primary"
                        icon={<FaPlus />}
                        onClick={handleAdd}
                    >
                        Add Specialty
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={specialtiesWithCount}
                    rowKey="id"
                    pagination={{
                        pageSize: 15,
                        showTotal: (total) => `Total ${total} specialties`,
                    }}
                />
            </Card>

            <Modal
                title={editingSpecialty ? 'Edit Specialty' : 'Add Specialty'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="Specialty Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter specialty name' }]}
                    >
                        <Input placeholder="e.g., Cardiologist" />
                    </Form.Item>

                    <div className="form-actions">
                        <Button onClick={() => setIsModalVisible(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={creating || updating}>
                            {editingSpecialty ? 'Update' : 'Create'} Specialty
                        </Button>
                    </div>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default Specialites;
